import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../firebaseConfig';
import API_CONFIG from '../config/api';

let configured = false;
const AUTH_FAILURE_EVENT = 'apiAuthenticationFailed';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _firebaseAuthRetry?: boolean;
};

const isBackendUrl = (url = ''): boolean =>
  url.startsWith(API_CONFIG.BASE_URL) || url.startsWith('/api/');

/**
 * Firebase restores persisted sessions asynchronously. Waiting here prevents
 * the first protected request from racing auth initialization and leaving
 * without an Authorization header.
 */
export const getFirebaseIdToken = async (forceRefresh = false): Promise<string | null> => {
  await auth.authStateReady();
  return auth.currentUser?.getIdToken(forceRefresh) || null;
};

const setAuthorizationHeader = (config: InternalAxiosRequestConfig, token: string): void => {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    } as typeof config.headers;
  }
};

const notifyAuthenticationFailure = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_FAILURE_EVENT));
  }
};

/** Attach the signed-in Firebase user's ID token to backend API calls. */
export const configureApiAuthentication = (): void => {
  if (configured) return;
  configured = true;

  axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await getFirebaseIdToken();
    if (token) {
      setAuthorizationHeader(config, token);
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableRequestConfig | undefined;
      if (error.response?.status !== 401 || !config || !isBackendUrl(config.url || '')) {
        return Promise.reject(error);
      }

      // A second 401 means this is not a transient token-refresh problem.
      if (config._firebaseAuthRetry) {
        notifyAuthenticationFailure();
        return Promise.reject(error);
      }

      try {
        const token = await getFirebaseIdToken(true);
        if (!token) {
          notifyAuthenticationFailure();
          return Promise.reject(error);
        }
        config._firebaseAuthRetry = true;
        setAuthorizationHeader(config, token);
        return axios(config);
      } catch {
        notifyAuthenticationFailure();
        return Promise.reject(error);
      }
    }
  );

  if (typeof window === 'undefined') return;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!url.startsWith(API_CONFIG.BASE_URL)) return nativeFetch(input, init);

    const send = async (token: string | null): Promise<Response> => {
      if (!token) return nativeFetch(input, init);
      const headers = new Headers(input instanceof Request ? input.headers : undefined);
      new Headers(init.headers || {}).forEach((value, key) => headers.set(key, value));
      headers.set('Authorization', `Bearer ${token}`);
      return nativeFetch(input, { ...init, headers });
    };

    const response = await send(await getFirebaseIdToken());
    if (response.status !== 401) return response;

    try {
      const refreshedToken = await getFirebaseIdToken(true);
      if (!refreshedToken) {
        notifyAuthenticationFailure();
        return response;
      }
      const retryResponse = await send(refreshedToken);
      if (retryResponse.status === 401) notifyAuthenticationFailure();
      return retryResponse;
    } catch {
      notifyAuthenticationFailure();
      return response;
    }
  };
};
